import { useEffect, useState } from "react";
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    IconButton,
    Typography,
    Paper,
    InputAdornment,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Localized } from "@fluent/react";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import SaveIcon from "@mui/icons-material/Save";
import EventIcon from "@mui/icons-material/Event";
import { DocumentTest, DocumentTestAssignment, updateDocumentTest } from "../../../documentTests/documentTestsApi";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { isDocEditable, showDocComments } from "../../../documentSlice";
import { showError, showSuccess } from "../../../../notifications/notificationsSlice";
import { SectionComments } from "../../../SectionComments";

interface EditTestScheduleDialogProps {
    testSpecification: DocumentTest;
    documentId: number;
    sectionId: string;
    onSave: (test: DocumentTest) => void;
    onCancel: () => void;
}

export const EditTestScheduleDialog = ({
    testSpecification,
    documentId,
    sectionId,
    onSave,
    onCancel,
}: EditTestScheduleDialogProps) => {
    const dispatch = useAppDispatch();
    const editable = useAppSelector(isDocEditable);
    const showComments = useAppSelector((state) => showDocComments(state, sectionId));

    const [fullScreen, setFullScreen] = useState(true);
    const toggleFullScreen = () => setFullScreen((prev) => !prev);

    const spec = testSpecification?.testSpecification;
    const testName = spec?.name ?? "";
    const testDescription = spec?.description ?? "";

    const [plannedStart, setPlannedStart] = useState<string>(testSpecification?.plannedStart ?? "");
    const [plannedEnd, setPlannedEnd] = useState<string>(testSpecification?.plannedEnd ?? "");

    const [testerName, setTesterName] = useState("");
    const [testerEmail, setTesterEmail] = useState("");
    const [approverName, setApproverName] = useState("");
    const [approverEmail, setApproverEmail] = useState("");

    useEffect(() => {
        const a = testSpecification?.assignments || [];
        const pick = (role: "TESTER" | "APPROVER") => a.find((x) => x.role === role);
        const ex = pick("TESTER");
        const ap = pick("APPROVER");
        if (ex) {
            setTesterName(ex.name ?? "");
            setTesterEmail(ex.email ?? "");
        }
        if (ap) {
            setApproverName(ap.name ?? "");
            setApproverEmail(ap.email ?? "");
        }
    }, [testSpecification]);

    const [saving, setSaving] = useState(false);

    const buildAssignments = (): DocumentTestAssignment[] => {
        const list: DocumentTestAssignment[] = [];
        if (testerName || testerEmail) {
            list.push({
                role: "TESTER",
                name: testerName || undefined,
                email: testerEmail || undefined,
            });
        }
        if (approverName || approverEmail) {
            list.push({
                role: "APPROVER",
                name: approverName || undefined,
                email: approverEmail || undefined,
            });
        }
        return list;
    };

    const handleSave = async () => {
        if (plannedStart && plannedEnd && plannedEnd < plannedStart) {
            dispatch(showError("Plan koniec nie może być wcześniejszy niż plan start."));
            return;
        }

        setSaving(true);
        try {
            const assignments = buildAssignments();

            const updated: DocumentTest = {
                ...testSpecification,
                plannedStart: plannedStart || null,
                plannedEnd: plannedEnd || null,
                assignments,
                testSpecification: {
                    ...spec!,
                    name: testName,
                    description: testDescription ?? "",
                },
            };

            const result = await dispatch(updateDocumentTest(updated));
            if (updateDocumentTest.fulfilled.match(result)) {
                dispatch(showSuccess("Zapisano zmiany."));
                onSave(result.payload as DocumentTest);
            } else {
                const errorMsg =
                    // @ts-ignore
                    (result?.payload?.problem?.title as string) ||
                    // @ts-ignore
                    result?.payload ||
                    "error";
                dispatch(showError(errorMsg));
            }
        } catch {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    const content = (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", p: 1 }}>
            <Box display="flex" flexDirection="column" gap={2} sx={{ flex: 1, overflow: "auto", p: 2 }}>
                {/* Nagłówek: nazwa i opis (tylko podgląd) */}
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        Test
                    </Typography>
                    <TextField
                        label="Nazwa"
                        value={testName}
                        fullWidth
                        InputProps={{ readOnly: true }}
                    />
                    <Typography variant="caption" fontWeight={700} marginTop={2} color="text.secondary">
                        Opis testu
                    </Typography>

                    <Box
                        sx={{ mt: 0.5 }}
                        dangerouslySetInnerHTML={{ __html: testDescription }}
                    />
                </Paper>

                {/* Planowane daty */}
                <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 2 }}>
                    <Box
                        px={1.5}
                        py={1}
                        bgcolor={(t) => t.palette.grey[100]}
                        borderBottom={(t) => `1px solid ${t.palette.divider}`}
                    >
                        <Typography variant="subtitle2" fontWeight={700}>
                            Planowane daty
                        </Typography>
                    </Box>

                    <Box p={2} display="grid" gap={2} gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}>
                        <TextField
                            label="Plan start"
                            type="date"
                            value={plannedStart ?? ""}
                            onChange={(e) => setPlannedStart(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EventIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                                readOnly: !editable,
                            }}
                            fullWidth
                        />
                        <TextField
                            label="Plan koniec"
                            type="date"
                            value={plannedEnd ?? ""}
                            onChange={(e) => setPlannedEnd(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EventIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                                readOnly: !editable,
                            }}
                            fullWidth
                        />
                    </Box>
                </Paper>


            </Box>
        </Box>
    );

    return (
        <Dialog
            open
            fullWidth
            maxWidth={fullScreen ? undefined : "lg"}
            fullScreen={fullScreen}
            PaperProps={{
                sx: (t) => ({
                    border: `1px solid ${t.palette.divider}`,
                    boxShadow: t.shadows[8],
                    overflow: "hidden",
                    ...(fullScreen ? { m: 2, height: "calc(100% - 32px)", borderRadius: 3 } : { borderRadius: 2 }),
                }),
            }}
        >
            <DialogTitle
                sx={(t) => ({
                    bgcolor: t.palette.grey[200],
                    borderBottom: `1px solid ${t.palette.divider}`,
                    py: 1.25,
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                })}
            >
                Edycja planu testu
                <IconButton onClick={toggleFullScreen} style={{ float: "right" }} color="primary">
                    {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {showComments ? (
                    <SectionComments
                        sectionId={sectionId}
                        contextType="test"
                        contextGuid={testSpecification?.guid ?? null}
                        component={content}
                    />
                ) : (
                    content
                )}
            </DialogContent>

            <DialogActions>
                <LoadingButton
                    loading={saving}
                    onClick={handleSave}
                    startIcon={<SaveIcon />}
                    size="small"
                    color="success"
                    variant="contained"
                    disabled={!editable}
                >
                    <Localized id="save">
                        <span>_Zapisz</span>
                    </Localized>
                </LoadingButton>
                <LoadingButton loading={saving} onClick={onCancel} size="small" variant="outlined">
                    <Localized id="cancel">
                        <span>_Anuluj</span>
                    </Localized>
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default EditTestScheduleDialog;
